import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { ModeloFusionDialog } from "@/components/ModeloFusionDialog";
import { ModeloDuplicadosDialog } from "@/components/ModeloDuplicadosDialog";
import { AsignacionAnioDialog } from "@/components/AsignacionAnioDialog";
import { Paginacion } from "@/components/Paginacion";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { useAuth } from "@/lib/auth";
import { Pencil, X, GitMerge, Copy, CalendarClock, FilterX } from "lucide-react";

const VACIO = { marca: "", modelo: "", anio_inicio: "", anio_fin: "", intervalo_aceite_km: "", grupo_tarifa_id: "", grupo_cilindrada_id: "" };
const FILTROS_VACIOS = { marca: "", grupo_cilindrada_id: "", grupo_tarifa_id: "", anios: "", catalogo: "", vehiculos: "" };

const Campo = ({ label, obligatorio, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

export default function Modelos() {
  const [modelos, setModelos] = useState([]);
  const [q] = useBusquedaLive("Buscar en modelos…");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);
  const [form, setForm] = useState(VACIO);
  const [editId, setEditId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [gruposCil, setGruposCil] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [fusionAbierto, setFusionAbierto] = useState(false);
  const [fusionIds, setFusionIds] = useState([]);
  const [dupAbierto, setDupAbierto] = useState(false);
  const [asigAnioAbierto, setAsigAnioAbierto] = useState(false);
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [opcionesFiltro, setOpcionesFiltro] = useState(null);
  const [grupoAsignar, setGrupoAsignar] = useState("");
  const [asignando, setAsignando] = useState(false);
  const { isAdmin } = useAuth();

  const toggleSel = (id) => setSel((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const abrirFusion = (ids) => { setFusionIds(ids); setFusionAbierto(true); };
  const trasFusion = () => { setSel(new Set()); setFusionAbierto(false); setDupAbierto(false); cargar(); cargarFiltros(); };

  const cargarGruposCil = () => api.get("/grupos-cilindrada").then((r) => setGruposCil(r.data)).catch(() => {});
  const cargarFiltros = () => api.get("/modelos/filtros").then((r) => setOpcionesFiltro(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/grupos-tarifa").then((r) => setGrupos(r.data)).catch(() => {});
    cargarGruposCil();
    cargarFiltros();
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/modelos", { params: { q: q.trim() || undefined, page, page_size: pageSize,
        marca: filtros.marca || undefined, grupo_cilindrada_id: filtros.grupo_cilindrada_id || undefined,
        grupo_tarifa_id: filtros.grupo_tarifa_id || undefined, anios: filtros.anios || undefined,
        catalogo: filtros.catalogo || undefined, vehiculos: filtros.vehiculos || undefined } });
      setModelos(data.items);
      setMeta({ total: data.total, total_pages: data.total_pages });
    } finally { setCargando(false); }
  }, [q, filtros, page, pageSize]);

  const grupoCilNombre = (m) => gruposCil.find((g) => g.id === m.grupo_cilindrada_id)?.nombre || "";
  const grupoTarifaLabel = (m) => {
    const g = grupos.find((x) => x.id === m.grupo_tarifa_id);
    return g ? `${g.nombre} (₡${g.tarifa_hora}/h)` : "—";
  };

  const setFiltro = (campo, v) => setFiltros((f) => ({ ...f, [campo]: v }));
  const limpiarFiltros = () => setFiltros(FILTROS_VACIOS);
  const hayFiltros = Object.values(filtros).some(Boolean);

  useEffect(() => { setPage(1); }, [q, filtros, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const asignarGrupoTarifa = async () => {
    if (!grupoAsignar || sel.size === 0) return;
    setAsignando(true);
    try {
      const { data } = await api.post("/modelos/grupo-tarifa", { modelo_ids: [...sel], grupo_tarifa_id: grupoAsignar });
      toast.success(`Grupo tarifario asignado a ${data.modificados} modelo(s)`);
      setSel(new Set()); setGrupoAsignar("");
      cargar(); cargarFiltros();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAsignando(false); }
  };

  const set = (campo, v) => setForm((f) => ({ ...f, [campo]: v }));

  const cancelar = () => { setEditId(null); setForm(VACIO); };

  const editar = (m) => {
    setEditId(m.id);
    setForm({ marca: m.marca, modelo: m.modelo, anio_inicio: m.anio_inicio ?? "", anio_fin: m.anio_fin ?? "",
      intervalo_aceite_km: m.intervalo_aceite_km ?? "", grupo_tarifa_id: m.grupo_tarifa_id || "",
      grupo_cilindrada_id: m.grupo_cilindrada_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const eliminar = async (m) => {
    if (!window.confirm(`¿Eliminar el modelo "${m.nombre_mostrar || `${m.marca} ${m.modelo}`}"?`)) return;
    try { await api.delete(`/modelos/${m.id}`); toast.success("Modelo eliminado"); if (editId === m.id) cancelar(); cargar(); cargarFiltros(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardar = async () => {
    const payload = { marca: form.marca, modelo: form.modelo,
      anio_inicio: form.anio_inicio ? Number(form.anio_inicio) : null,
      anio_fin: form.anio_fin ? Number(form.anio_fin) : null,
      intervalo_aceite_km: form.intervalo_aceite_km ? Number(form.intervalo_aceite_km) : null,
      grupo_tarifa_id: form.grupo_tarifa_id || null,
      grupo_cilindrada_id: form.grupo_cilindrada_id || null };
    setGuardando(true);
    try {
      if (editId) await api.patch(`/modelos/${editId}`, payload);
      else await api.post("/modelos", payload);
      toast.success(editId ? "Modelo actualizado" : "Modelo creado");
      cancelar();
      cargar(); cargarFiltros();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  return (
    <div data-testid="modelos-page" className="space-y-4">
      <Panel titulo={editId ? "Modelos — editando" : "Modelos"} testId="modelos-panel">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Campo label="Marca" obligatorio>
            <Input data-testid="modelo-marca-input" value={form.marca} placeholder="Ej: Honda"
              onChange={(e) => set("marca", e.target.value)} />
          </Campo>
          <Campo label="Modelo" obligatorio>
            <Input data-testid="modelo-modelo-input" value={form.modelo} placeholder="Ej: CBF150S"
              onChange={(e) => set("modelo", e.target.value)} />
          </Campo>
          <Campo label="Año inicio (opcional)">
            <Input data-testid="modelo-anio-inicio-input" inputMode="numeric" maxLength={4}
              value={form.anio_inicio} placeholder="Ej: 2018"
              onChange={(e) => set("anio_inicio", e.target.value.replace(/\D/g, "").slice(0, 4))} />
          </Campo>
          <Campo label="Año fin (vacío = actual)">
            <Input data-testid="modelo-anio-fin-input" inputMode="numeric" maxLength={4}
              value={form.anio_fin} placeholder="Ej: 2024"
              onChange={(e) => set("anio_fin", e.target.value.replace(/\D/g, "").slice(0, 4))} />
          </Campo>
          <Campo label="Cambio de aceite (km)">
            <Input data-testid="modelo-intervalo-aceite-input" inputMode="numeric" maxLength={6}
              value={form.intervalo_aceite_km} placeholder="Ej: 2000"
              onChange={(e) => set("intervalo_aceite_km", e.target.value.replace(/\D/g, "").slice(0, 6))} />
          </Campo>
          <Campo label="Grupo de cilindrada">
            <select data-testid="modelo-grupo-cilindrada-select" value={form.grupo_cilindrada_id}
              onChange={(e) => set("grupo_cilindrada_id", e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">Sin grupo</option>
              {gruposCil.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Grupo de tarifa (mano de obra)">
            <select data-testid="modelo-grupo-tarifa-select" value={form.grupo_tarifa_id}
              onChange={(e) => set("grupo_tarifa_id", e.target.value)}
              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">Sin grupo</option>
              {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre} (₡{g.tarifa_hora}/h)</option>)}
            </select>
          </Campo>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
          <p className="text-xs text-slate-500">
            Para generaciones, cree varias entradas del mismo modelo con distinto rango de años.
          </p>
          <div className="flex flex-wrap gap-2">
            {editId && (
              <Button variant="outline" data-testid="modelo-cancelar-btn"
                className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                onClick={cancelar}>Cancelar</Button>
            )}
            <Button data-testid="modelo-guardar-btn" onClick={guardar}
              disabled={guardando || !form.marca.trim() || !form.modelo.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
              {guardando ? "Guardando..." : editId ? "Guardar cambios" : "Agregar"}
            </Button>
          </div>
        </div>
      </Panel>

      <Panel titulo="Modelos registrados" testId="modelos-lista-panel">
        <div className="flex flex-wrap items-end gap-2 mb-4 pb-4 border-b border-slate-100">
          <select data-testid="modelos-filtro-marca" value={filtros.marca} onChange={(e) => setFiltro("marca", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Marca (todas)</option>
            {(opcionesFiltro?.marcas || []).map((o) => (
              <option key={o.valor} value={o.valor}>{o.valor} ({o.cantidad})</option>
            ))}
          </select>
          <select data-testid="modelos-filtro-anios" value={filtros.anios} onChange={(e) => setFiltro("anios", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Años (todos)</option>
            <option value="con">Con rango</option>
            <option value="sin">Sin rango</option>
          </select>
          <select data-testid="modelos-filtro-cilindrada" value={filtros.grupo_cilindrada_id}
            onChange={(e) => setFiltro("grupo_cilindrada_id", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Cilindrada (todas)</option>
            {(opcionesFiltro?.grupos_cilindrada || []).map((o) => (
              <option key={o.id} value={o.id}>{o.nombre} ({o.cantidad})</option>
            ))}
            {opcionesFiltro?.sin_cilindrada > 0 && <option value="sin">Sin cilindrada ({opcionesFiltro.sin_cilindrada})</option>}
          </select>
          <select data-testid="modelos-filtro-grupo-tarifa" value={filtros.grupo_tarifa_id}
            onChange={(e) => setFiltro("grupo_tarifa_id", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Grupo de tarifa (todos)</option>
            {(opcionesFiltro?.grupos_tarifa || []).map((o) => (
              <option key={o.id} value={o.id}>{o.nombre} ({o.cantidad})</option>
            ))}
            {opcionesFiltro?.sin_tarifa > 0 && <option value="sin">Sin grupo ({opcionesFiltro.sin_tarifa})</option>}
          </select>
          <select data-testid="modelos-filtro-catalogo" value={filtros.catalogo} onChange={(e) => setFiltro("catalogo", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Catálogo (todos)</option>
            {opcionesFiltro?.con_catalogo > 0 && <option value="con">Con catálogo ({opcionesFiltro.con_catalogo})</option>}
            {opcionesFiltro?.sin_catalogo > 0 && <option value="sin">Sin catálogo ({opcionesFiltro.sin_catalogo})</option>}
          </select>
          <select data-testid="modelos-filtro-vehiculos" value={filtros.vehiculos} onChange={(e) => setFiltro("vehiculos", e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">Vehículos (todos)</option>
            {opcionesFiltro?.con_vehiculos > 0 && <option value="con">Con vehículos ({opcionesFiltro.con_vehiculos})</option>}
            {opcionesFiltro?.sin_vehiculos > 0 && <option value="sin">Sin vehículos ({opcionesFiltro.sin_vehiculos})</option>}
          </select>
          {hayFiltros && (
            <Button size="sm" variant="outline" data-testid="modelos-limpiar-filtros-btn" onClick={limpiarFiltros}
              className="h-9 border-slate-300 text-slate-600 hover:bg-slate-50">
              <FilterX size={14} className="mr-1.5" /> Limpiar filtros
            </Button>
          )}
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {sel.size > 0 && (
              <>
                <select data-testid="modelos-asignar-grupo-select" value={grupoAsignar}
                  onChange={(e) => setGrupoAsignar(e.target.value)}
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
                  <option value="">Elegir grupo de tarifa…</option>
                  {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre} (₡{g.tarifa_hora}/h)</option>)}
                </select>
                <Button size="sm" data-testid="modelos-asignar-grupo-btn" disabled={!grupoAsignar || asignando}
                  onClick={asignarGrupoTarifa} className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40">
                  {asignando ? "Asignando..." : `Asignar grupo tarifario (${sel.size})`}
                </Button>
              </>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline" data-testid="modelos-asignacion-anio-btn"
                className="border-indigo-300 text-indigo-600 hover:bg-indigo-50" onClick={() => setAsigAnioAbierto(true)}>
                <CalendarClock size={14} className="mr-1.5" /> Revisar asignación por año
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid="modelos-buscar-duplicados-btn"
              className="border-slate-300 text-slate-600 hover:bg-slate-50" onClick={() => setDupAbierto(true)}>
              <Copy size={14} className="mr-1.5" /> Buscar posibles duplicados
            </Button>
            <Button size="sm" data-testid="modelos-fusionar-btn" disabled={sel.size < 2}
              onClick={() => abrirFusion([...sel])}
              className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40">
              <GitMerge size={14} className="mr-1.5" /> Fusionar seleccionados{sel.size >= 2 ? ` (${sel.size})` : ""}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="modelos-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold w-8">
                  <input type="checkbox" data-testid="modelos-sel-todos"
                    checked={modelos.length > 0 && modelos.every((m) => sel.has(m.id))}
                    onChange={(e) => setSel((prev) => {
                      const n = new Set(prev);
                      modelos.forEach((m) => (e.target.checked ? n.add(m.id) : n.delete(m.id)));
                      return n;
                    })} className="accent-indigo-600 w-4 h-4" />
                </th>
                <th className="py-2.5 pr-3 font-semibold">Marca</th>
                <th className="py-2.5 pr-3 font-semibold">Modelo</th>
                <th className="py-2.5 pr-3 font-semibold">Años</th>
                <th className="py-2.5 pr-3 font-semibold">Cilindrada</th>
                <th className="py-2.5 pr-3 font-semibold">Grupo de tarifa</th>
                <th className="py-2.5 pr-3 font-semibold">Vehículos</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {modelos.map((m) => (
                <tr key={m.id} data-testid={`modelo-row-${m.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${editId === m.id ? "bg-indigo-50/50" : ""} ${sel.has(m.id) ? "bg-indigo-50/40" : ""}`}>
                  <td className="py-2.5 pr-3">
                    <input type="checkbox" data-testid={`modelo-sel-${m.id}`} checked={sel.has(m.id)}
                      onChange={() => toggleSel(m.id)} className="accent-indigo-600 w-4 h-4" />
                  </td>
                  <td className="py-2.5 pr-3 font-medium text-slate-800">
                    <div className="flex items-center gap-1.5">
                      {m.marca}
                      {m.tiene_catalogo && (
                        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Con catálogo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 text-slate-700">{m.modelo}</td>
                  <td className="py-2.5 pr-3 text-slate-600">
                    {m.anio_inicio ? `${m.anio_inicio}–${m.anio_fin || "actual"}` : "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600" data-testid={`modelo-grupo-cil-${m.id}`}>
                    {grupoCilNombre(m) || "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600" data-testid={`modelo-grupo-tarifa-${m.id}`}>
                    {m.grupo_tarifa_id ? grupoTarifaLabel(m) : (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        Sin grupo
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-600" data-testid={`modelo-vehiculos-${m.id}`}>{m.vehiculos}</td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`modelo-editar-btn-${m.id}`} title="Editar" onClick={() => editar(m)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Pencil size={13} />
                      </button>
                      <button data-testid={`modelo-eliminar-btn-${m.id}`} title="Eliminar" onClick={() => eliminar(m)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {modelos.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400" data-testid="modelos-vacio">
                  No se encontraron modelos
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>

      <ModeloFusionDialog abierto={fusionAbierto} ids={fusionIds}
        onCerrar={() => setFusionAbierto(false)} onFusionado={trasFusion} />
      <ModeloDuplicadosDialog abierto={dupAbierto} onCerrar={() => setDupAbierto(false)}
        onFusionarGrupo={(ids) => { setDupAbierto(false); abrirFusion(ids); }} />
      <AsignacionAnioDialog abierto={asigAnioAbierto} onCerrar={() => setAsigAnioAbierto(false)}
        onAplicado={cargar} />
    </div>
  );
}
